import {
  authSessionResponseSchema,
  latestPriceResponseSchema,
  pdfGenerationResponseSchema,
  pricelistCatalogResponseSchema,
  pricelistGridResponseSchema,
  webWorkspaceListResponseSchema,
  webWorkspaceResponseSchema,
  type AuthSessionResponseDto,
  type CatalogResponseDto,
  type LatestPriceResponseDto,
  type PdfGenerationResponseDto,
  type PricelistGridDto,
  type WebWorkspaceRecordDto,
  type WebWorkspaceSummaryDto
} from "./schemas";
import type { ProductJson, QuotationRequest, WorkspaceState } from "../domain/quotation";

const maxInlineErrorLength = 180;

async function fetchJson<T>(
  input: RequestInfo | URL,
  schema: { parse: (value: unknown) => T },
  init?: RequestInit
): Promise<T> {
  const response = await fetch(input, {
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    },
    ...init
  });

  const text = await response.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }

  if (!response.ok) {
    const message = responseErrorMessage(response.status, data, text);
    throw new Error(message);
  }

  return schema.parse(data);
}

function responseErrorMessage(status: number, data: unknown, text: string): string {
  if (typeof data === "object" && data !== null && "error" in data) {
    return String((data as { error: unknown }).error);
  }

  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return `Request failed with status ${status}`;
  }

  if (looksLikeHtml(trimmed)) {
    return status === 503
      ? "Server is temporarily unavailable. Please try again."
      : `Request failed with status ${status}. Please try again.`;
  }

  if (trimmed.length > maxInlineErrorLength) {
    return `${trimmed.slice(0, maxInlineErrorLength)}...`;
  }

  return trimmed;
}

function looksLikeHtml(value: string): boolean {
  return /^<!doctype html/i.test(value) || /^<html[\s>]/i.test(value);
}

export function getCatalog(): Promise<CatalogResponseDto> {
  return fetchJson("/api/web/catalog", pricelistCatalogResponseSchema);
}

export async function getPricelistGrids(): Promise<PricelistGridDto[]> {
  const response = await fetchJson("/api/web/catalog/grids", pricelistGridResponseSchema);
  return response.grids;
}

export function generateQuotationPdf(
  request: QuotationRequest
): Promise<PdfGenerationResponseDto> {
  return fetchJson("/api/web/generate-quotation-pdf", pdfGenerationResponseSchema, {
    method: "POST",
    body: JSON.stringify(request)
  });
}

export function generateProformaPdf(
  request: QuotationRequest
): Promise<PdfGenerationResponseDto> {
  return fetchJson("/api/web/generate-proforma-pdf", pdfGenerationResponseSchema, {
    method: "POST",
    body: JSON.stringify(request)
  });
}

export interface LatestPriceInput {
  brand: string;
  product: ProductJson;
  discount: number;
}

export function getLatestPrice(
  input: LatestPriceInput
): Promise<LatestPriceResponseDto> {
  return fetchJson("/api/web/latest-price", latestPriceResponseSchema, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function getAuthSession(): Promise<AuthSessionResponseDto> {
  return fetchJson("/api/web/auth/session", authSessionResponseSchema);
}

export function login(password: string): Promise<AuthSessionResponseDto> {
  return fetchJson("/api/web/auth/login", authSessionResponseSchema, {
    method: "POST",
    body: JSON.stringify({ password })
  });
}

export function logout(): Promise<AuthSessionResponseDto> {
  return fetchJson("/api/web/auth/logout", authSessionResponseSchema, {
    method: "POST",
    body: JSON.stringify({})
  });
}

export interface SaveWorkspaceInput {
  id?: string;
  title: string;
  state: WorkspaceState;
  lastPdfUrl?: string;
  lastPdfNumber?: string;
}

export async function listWorkspaces(): Promise<WebWorkspaceSummaryDto[]> {
  const response = await fetchJson(
    "/api/web/workspaces",
    webWorkspaceListResponseSchema
  );
  return response.workspaces;
}

export async function getWorkspace(id: string): Promise<WebWorkspaceRecordDto> {
  const response = await fetchJson(
    `/api/web/workspaces/${encodeURIComponent(id)}`,
    webWorkspaceResponseSchema
  );
  return response.workspace;
}

export async function saveWorkspace(
  input: SaveWorkspaceInput
): Promise<WebWorkspaceRecordDto> {
  const body = JSON.stringify({
    title: input.title,
    state: input.state,
    last_pdf_url: input.lastPdfUrl,
    last_pdf_number: input.lastPdfNumber
  });
  const response = await fetchJson(
    input.id
      ? `/api/web/workspaces/${encodeURIComponent(input.id)}`
      : "/api/web/workspaces",
    webWorkspaceResponseSchema,
    {
      method: input.id ? "PUT" : "POST",
      body
    }
  );
  return response.workspace;
}
